import { useState, useEffect, useMemo } from "react";
import { sortMonths } from "../utils";

const getMonths = (json) => {
    if(!json) {
        return [];
    }

    const monthsSet = new Set();
    const forecastArr = (json && typeof json === 'object' && !Array.isArray(json)) ? Object.values(json) : (Array.isArray(json) ? json : []);

    forecastArr.forEach(item => {
        if(item && item.mes) {
            monthsSet.add(item.mes);
        }
    });

    return sortMonths(Array.from(monthsSet));
}

//Capacidad-view data pipeline: detected months, capacity/excess grouping (processData), and the
//percentage-sorted list ProgressBar reads from (filterMonths). getPercentages is passed down to ProgressBar
//as onPercentageChange, and its result feeds back into filterMonths's own sort order.
export const useCapacidadData = ({json, activeOption, activeContainer, selectFilterMonths, selectedArea, setDetectedMonths}) => {
    const [percentages, setPercentages] = useState({});

    useEffect(() => {
        let months = [];
        if (activeOption.uniqueMonths && activeOption.uniqueMonths.length > 0) {
            months = activeOption.uniqueMonths;
        } else if (json && activeContainer === "Capacidad") {
            //This will now work as a fallback
            months = getMonths(json);
        }

        if (months.length > 0) {
            const sortedMonths = sortMonths(months);
            setDetectedMonths(sortedMonths);
        } else {
            setDetectedMonths([]);
        }

    }, [activeOption, activeContainer, json])

    //Gives format to the information to show it on charts
    const processData = useMemo(() => {
        const grouped = {};

        const forecastArr = (json && typeof json === 'object' && !Array.isArray(json))  //Make sure it's an array
            ? Object.values(json)
            : (Array.isArray(json) ? json : []);

        if (!Array.isArray(forecastArr) || forecastArr.length === 0) {
            return [];
        }

        const updatedForecastArr = forecastArr.map(item => ({
            ...item, area: selectedArea[item.nombre] || item.area   //Updates the groups with the user's selection
        }))

        updatedForecastArr.forEach(item => {
            if (item && item.nombre && item.area && Array.isArray(item.valores)) {
                const key = `${item.nombre}-${item.area}`;  //Group elements by name-area

                if (!grouped[key]) {
                    grouped[key] = {    //Creates an array with default values for each group
                        nombre: item.nombre,
                        area: item.area,
                        setup: 0,
                        valores: [0, 0, 0],
                        totalSum: 0,
                        contributingMonths: new Set()
                    };
                }

                const shouldSum = selectFilterMonths.includes(item.mes);

                if(shouldSum) {
                    item.valores.forEach((val, index) => {
                        if (typeof val === 'number' && !isNaN(val) && index < 3) {
                                grouped[key].valores[index] += val; //Sum values according to the turn
                                grouped[key].totalSum += val;   //Calculates total value, needed to get the excess
                        }
                    });

                    if(typeof item.setup === 'number' && !isNaN(item.setup)) {
                        grouped[key].setup += item.setup;
                    }

                    if(item.mes) {
                        grouped[key].contributingMonths.add(item.mes);
                    }
                }
            }
        });

        const data = Object.values(grouped).map(item => {
            const totalMax = item.contributingMonths.size > 0 ? 43200 * item.contributingMonths.size : 43200; //Gets max value for a name-area group according to contributingMonths. Used to calculate surplus

            let excess = 0;

            if(item.totalSum > totalMax) {  //Calculates the exceeding time
                excess = item.totalSum - totalMax;
            }

            return {...item, valores: [...item.valores, excess]} //Adds the fourth value (surplus) to the values array
        })
        return data;
    }, [selectFilterMonths, json, selectedArea]);   //Recalculate if a month is selected

    const getPercentages = (name, percentage) => {
        setPercentages(prev => ({
            ...prev, [name]: percentage
        }));
    }

    const filterMonths = useMemo(() => {
        if (!selectFilterMonths || selectFilterMonths.length === 0) {
            return [];
        }

        return [...processData].filter(item => {
            const totalSum = item.valores.reduce((sum, val) => sum + val, 0);
            return totalSum > 0 && item.contributingMonths.size > 0;
        }).sort ((a, b) => {
            //Get the percentage for each item to sort it in descending order
            const percentagesA = percentages[a.nombre] || 0;
            const percentagesB = percentages[b.nombre] || 0;
            return percentagesB - percentagesA
        });
    }, [processData, selectFilterMonths, percentages]);

    return {filterMonths, getPercentages};
}